import { CancelButton, GroupButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import { useApiUtils } from '@core/util/apiUtils';
import { permissaoUtils } from '@core/util/permissaoUtil';
import FuseUtils from '@fuse/utils';
import { VisualizarCategoriasCompra } from 'app/main/compra/components/VisualizarCategoriasCompra';
import { VendaContext } from 'app/main/venda/VendaContext';
import { fechamentoPeriodoApi } from 'app/services/api/fechamentoPeriodoApi';
import * as Actions from 'app/store/actions';
import moment from 'moment';
import React, { useContext } from 'react';
import { FaTags } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import VisualizarDadosEntrega from '../../DadosEntrega/VisualizarDadosEntrega';
import VisualizarDadosVenda from '../../DadosVenda/VisualizarDadosVenda';
import HistoricoSituacaoVenda from '../../HistoricoSituacaoVenda';
import { VisualizarVendaFormaPagamento } from '../../VendaFormaPagamento';
import VisualizarProdutoServico from '../../VendaProdutoServico/visualizar/VisualizarProdutoServico';
import { VendaModais } from '../VendaModais';
import { visualizarVendaStyles } from './styles';

const getCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const VisualizarVendaModal = ({ emitirPedidoVenda }) => {
  const [modal, setModal] = useContext(VendaContext).modal;
  const [venda, setVendaAtual] = useContext(VendaContext).vendaAtual;
  const [, setLoading] = useContext(VendaContext).loading;

  const styles = visualizarVendaStyles();
  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const handleCloseModal = () => {
    setModal(false);
  };

  const handleOpenEditMode = async (venda) => {
    setLoading(true);
    const response = await fechamentoPeriodoApi.buscarUltimoPeriodo('RECEBIMENTOS_VENDAS', venda.data);
    if (response.data && response.data.bloquearAnteriores) {
      setLoading(false);
      const vendaOp = venda.produtosServicos.filter((produtoServico) => produtoServico.produto);
      if (
        moment(venda.data).isSameOrBefore(response.data.periodo) &&
        response.data.funcionalidades.includes('RECEBIMENTOS_VENDAS')
      ) {
        if (vendaOp.length > 0) {
          throw apiUtils.handleApiError({
            message:
              'A venda de produto não pode ser editada porque a data refere-se a um período já fechado no sistema e não pode haver movimentações de estoque.',
          });
        } else {
          throw apiUtils.handleApiError({
            message:
              'Não é possível lançar, editar e excluir vendas com datas referentes a um período já fechado no sistema.',
          });
        }
      }
    }

    if (venda.situacao === 'VENDA_APROVADA') {
      if (venda?.possuiCobrancaBaixadaOuRemessaGerada) {
        setLoading(false);
        apiUtils.handleApiError({
          message:
            'A venda não pode ser editada porque já teve baixa ou alguma cobrança está em processo de geração de remessa.',
        });
      } else {
        setLoading(false);
        setModal(VendaModais.cadastrarVenda);
      }
    } else {
      setLoading(false);
      setModal(VendaModais.cadastrarVenda);
    }
  };

  const Content = () => {
    return (
      <>
        <VisualizarDadosVenda venda={venda} />

        <VisualizarProdutoServico venda={venda} styles={styles} getCurrency={getCurrency} />

        <VisualizarDadosEntrega venda={venda} getCurrency={getCurrency} />

        <VisualizarCategoriasCompra values={venda} />

        <VisualizarVendaFormaPagamento venda={venda} styles={styles} getCurrency={getCurrency} />

        {venda?.historico && <HistoricoSituacaoVenda historico={venda.historico} />}
      </>
    );
  };

  const clonarVenda = async () => {
    const clone = {};

    // Dados da venda
    clone.data = venda?.data;
    clone.cliente = venda?.cliente;
    clone.situacao = venda?.situacao;
    clone.descricao = venda?.descricao;
    clone.municipioIncidencia = venda?.municipioIncidencia;

    // Categorias
    clone.categorias = venda?.categorias?.map((categoria) => {
      return {
        id: undefined,
        classe: { ...categoria.classe },
        valorBruto: categoria.valorBruto,
      };
    });

    // Produtos e Serviços
    clone.produtosServicos = venda?.produtosServicos?.map((produtoServico) => {
      return {
        key: FuseUtils.generateGUID(),
        produto: produtoServico.produto,
        servico: produtoServico.servico,
        quantidade: produtoServico.quantidade,
        valor: produtoServico.valor,
        servicoFiscal: produtoServico?.servicoFiscal,
        fiscal: { ...produtoServico?.fiscal, id: null },
      };
    });

    // Dados de entrega
    clone.valorFrete = venda?.valorFrete;
    clone.tipoDesconto = venda?.tipoDesconto;
    clone.valorDesconto = venda?.valorDesconto;
    clone.valorTotal = venda?.valorTotal;
    clone.transportadora = venda?.transportadora;
    clone.vendedor = venda?.vendedor;
    clone.previsaoEntrega = venda?.previsaoEntrega;
    clone.dataVencimentoOrcamento = venda?.dataVencimentoOrcamento;

    // Forma de pagamento
    clone.pagamento = {
      tipoPagamento: venda?.pagamento?.tipoPagamento,
      formaRecebimento: venda?.pagamento?.formaRecebimento,
    };
    if (venda?.pagamento?.avista) {
      clone.pagamento.avista = { dataVencimento: venda.pagamento.avista.dataVencimento };
    } else if (venda?.pagamento?.parcelamento) {
      clone.pagamento.parcelamento = {
        parcelas: venda?.pagamento?.parcelamento?.parcelas?.map((parcela) => {
          return {
            dataVencimento: parcela.dataVencimento,
            valor: parcela.valor,
          };
        }),
      };
    }
    clone.vencimentoCobranca = venda?.vencimentoCobranca;
    clone.envioAutomaticoCobranca = venda?.envioAutomaticoCobranca;
    clone.enviarCobrancaEmail = venda?.enviarCobrancaEmail;
    clone.enviarNotaFiscalEmail = venda?.enviarNotaFiscalEmail;
    clone.faturarContratoVendaJuntos = venda?.faturarContratoVendaJuntos;
    clone.unificarCobrancaContratoVenda = venda?.unificarCobrancaContratoVenda;

    if (venda?.emailsCliente?.length > 0) {
      clone.emailsCliente = venda.emailsCliente.forEach((emailCliente) => {
        return { email: emailCliente.email, principal: emailCliente.principal, empresaId: emailCliente.empresaId };
      });
    }

    // Informações complementares
    clone.informacoesComplementares = venda?.informacoesComplementares;

    clone.isClone = true;
    setVendaAtual(clone);
    setModal(VendaModais.cadastrarVenda);
  };

  const actions = [];

  if (permissaoUtils.venda().editar) {
    actions.push({ label: 'Editar', onClick: () => handleOpenEditMode(venda) });
  }

  if (venda?.situacao === 'VENDA_APROVADA') {
    actions.push({ label: 'Emitir pedido de venda', onClick: () => emitirPedidoVenda(venda) });
  }

  if (permissaoUtils.venda().cadastrar) {
    actions.push({ label: 'Clonar', onClick: clonarVenda });
  }

  if (venda?.situacao === 'EM_ORCAMENTO') {
    actions.push({ label: 'Visualizar orçamento', onClick: () => emitirPedidoVenda(venda, 'orcamento') });
  }

  return (
    <SimpleDialog
      open={modal === VendaModais.visualizarVenda}
      onClose={handleCloseModal}
      paperClass={styles.dialogPaper}
    >
      <SimpleDialogContent
        header={{
          title: 'Cadastro de venda',
          icon: <FaTags />,
          toolbar: (
            <>
              <CancelButton onClick={handleCloseModal}>Fechar</CancelButton>
              <GroupButton actions={actions} label="Ações" />
            </>
          ),
        }}
        content={<Content />}
      />
    </SimpleDialog>
  );
};

export default VisualizarVendaModal;
